"use client";

import React, { useState, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Sparkles,
  ArrowRight,
  ChevronRight,
  X,
  Compass,
  Layers,
  BookOpen,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { imageForCategory, imageForSlug } from "@/lib/categoryImages";
import { getSubcategoryUrl } from "@/lib/categoryUrls";
import { getSubcategoryIcon } from "@/lib/subcategoryIcons";
import { POP_CULTURE_PILLARS } from "@/data/franchiseClusters";
import { FRANCHISE_SUBGROUPS, SubgroupItem } from "@/data/franchiseSubgroups";
import { REAL_NAME_PILLARS } from "@/data/realNameClusters";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

interface SubcategoryItem {
  name: string;
  slug: string;
  description?: string;
  names?: Array<string | { name: string; meaning?: string }>;
  count?: number;
  icon?: string;
}

interface CategoryHubClientProps {
  category: {
    name: string;
    slug: string;
    icon?: string;
    description?: string;
    subcategories: SubcategoryItem[];
  };
}

export default function CategoryHubClient({ category }: CategoryHubClientProps) {
  const [search, setSearch] = useState("");
  const [selectedLetter, setSelectedLetter] = useState("all");
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [activeUniverseDrawer, setActiveUniverseDrawer] = useState<{
    franchiseName: string;
    subgroups: SubgroupItem[];
  } | null>(null);
  const [drawerSearch, setDrawerSearch] = useState("");

  // Featured top 6 flagship subcategories
  const featured = useMemo(() => {
    return category.subcategories.slice(0, 6);
  }, [category.subcategories]);

  // Filtered subcategories for alphabetical archive
  const filtered = useMemo(() => {
    let result = category.subcategories;

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(
        (sub) =>
          sub.name.toLowerCase().includes(q) ||
          sub.slug.toLowerCase().includes(q) ||
          (sub.description && sub.description.toLowerCase().includes(q))
      );
    }

    if (selectedLetter !== "all") {
      result = result.filter((sub) => {
        const firstLetter = sub.name.charAt(0).toUpperCase();
        return firstLetter === selectedLetter;
      });
    }

    return result;
  }, [category.subcategories, search, selectedLetter]);

  // Group by alphabetical letter
  const groupedByLetter = useMemo(() => {
    const map = new Map<string, SubcategoryItem[]>();
    for (const sub of filtered) {
      const letter = sub.name.charAt(0).toUpperCase();
      const key = /^[A-Z]$/.test(letter) ? letter : "#";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(sub);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  // Filtered Franchise Pillars (for Pop Culture)
  const filteredPillars = useMemo(() => {
    if (!search.trim()) return POP_CULTURE_PILLARS;
    const q = search.toLowerCase().trim();

    return POP_CULTURE_PILLARS.map((pillar) => ({
      ...pillar,
      franchises: pillar.franchises.filter(
        (f) =>
          f.name.toLowerCase().includes(q) ||
          f.slug.toLowerCase().includes(q) ||
          pillar.title.toLowerCase().includes(q) ||
          pillar.shortTitle.toLowerCase().includes(q)
      ),
    })).filter((p) => p.franchises.length > 0);
  }, [search]);

  // Filtered Cultural Region Pillars (for Real Names)
  const filteredRealPillars = useMemo(() => {
    let list = REAL_NAME_PILLARS;
    if (selectedRegion !== "all") {
      list = list.filter((p) => p.id === selectedRegion);
    }
    if (!search.trim()) return list;
    const q = search.toLowerCase().trim();
    return list
      .map((p) => ({
        ...p,
        subcategories: p.subcategories.filter(
          (s) =>
            s.name.toLowerCase().includes(q) ||
            s.slug.toLowerCase().includes(q) ||
            p.name.toLowerCase().includes(q) ||
            p.shortName.toLowerCase().includes(q)
        ),
      }))
      .filter((p) => p.subcategories.length > 0);
  }, [selectedRegion, search]);

  // Filtered drawer subgroups
  const filteredDrawerSubgroups = useMemo(() => {
    if (!activeUniverseDrawer) return [];
    if (!drawerSearch.trim()) return activeUniverseDrawer.subgroups;
    const q = drawerSearch.toLowerCase().trim();
    return activeUniverseDrawer.subgroups.filter(
      (s) => s.name.toLowerCase().includes(q) || s.fullName.toLowerCase().includes(q)
    );
  }, [activeUniverseDrawer, drawerSearch]);

  return (
    <div className="w-full space-y-12">
      {/* Category Hero Banner */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative rounded-3xl overflow-hidden border border-border/80 bg-card shadow-2xl"
      >
        <div className="relative h-52 sm:h-72 w-full overflow-hidden">
          <Image
            src={imageForCategory(category.slug)}
            alt={`${category.name} visual realm atmosphere`}
            fill
            priority
            sizes="(max-width: 1200px) 100vw, 1200px"
            className="object-cover brightness-[0.7] dark:brightness-[0.55]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/75 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-card/90 via-card/50 to-transparent" />
        </div>

        <div className="relative -mt-20 sm:-mt-24 px-6 sm:px-10 pb-8">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
            <div className="flex items-center gap-4 sm:gap-5">
              <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-background/90 backdrop-blur-md border border-border/80 shadow-2xl flex items-center justify-center text-3xl sm:text-4xl shrink-0">
                {category.icon || "✨"}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="fantasy" className="text-[11px] uppercase tracking-wider">
                    Realm Archive
                  </Badge>
                </div>
                <h1 className="text-2xl sm:text-4xl font-bold font-fantasy text-foreground tracking-tight drop-shadow-sm">
                  {category.name}
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs px-3 py-1 bg-background/60 backdrop-blur-md">
                Collections
              </Badge>
              <Badge variant="secondary" className="text-xs px-3 py-1">
                Names &amp; Lore
              </Badge>
            </div>
          </div>

          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-3xl">
            {category.description}
          </p>
        </div>
      </motion.div>

      {/* Featured / Flagship Highlights */}
      {!search && selectedLetter === "all" && featured.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <h2 className="text-lg sm:text-xl font-bold font-fantasy text-foreground">
                Featured {category.name}
              </h2>
            </div>
            <span className="text-xs text-muted-foreground">Popular collections</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {featured.map((sub, i) => {
              const icon = getSubcategoryIcon(sub.name || sub.slug, category.slug);
              return (
                <motion.div
                  key={sub.slug}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.04 }}
                >
                  <Link
                    href={getSubcategoryUrl(category.slug, sub.slug)}
                    className="group block h-full rounded-2xl border border-border/80 bg-card/80 hover:border-primary/50 hover:shadow-lg transition-all duration-300 overflow-hidden"
                  >
                    <div className="relative h-24 w-full overflow-hidden">
                      <Image
                        src={imageForSlug(category.slug, sub.slug)}
                        alt={sub.name}
                        fill
                        sizes="(max-width: 768px) 50vw, 200px"
                        className="object-cover group-hover:scale-108 transition-transform duration-500 brightness-90"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
                      <div className="absolute top-2 left-2 text-xl filter drop-shadow">
                        {icon}
                      </div>
                    </div>

                    <div className="p-3 flex items-center justify-between">
                      <div className="min-w-0 pr-1">
                        <h3 className="text-xs font-bold text-foreground group-hover:text-primary transition-colors truncate">
                          {sub.name}
                        </h3>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          Explore realm
                        </p>
                      </div>
                      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>
      )}

      {/* SECTION 1: UNIVERSE REALMS (FOR POP CULTURE) */}
      {category.slug === "pop-culture" && (
        <section className="space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-primary" />
              <h2 className="text-lg sm:text-xl font-bold font-fantasy text-foreground">
                Universe &amp; Franchise Hubs
              </h2>
            </div>
            <span className="text-xs text-muted-foreground">Select a universe to explore species</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7">
            {filteredPillars.map((pillar) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl border border-border/80 bg-card/90 dark:bg-[#0c1424]/90 backdrop-blur-xl overflow-hidden shadow-xl flex flex-col hover:border-primary/40 transition-all duration-300"
              >
                {/* Universe Card Header with Custom Artwork */}
                <div className="relative h-36 sm:h-40 w-full overflow-hidden shrink-0 group">
                  <Image
                    src={pillar.image}
                    alt={`${pillar.shortTitle} universe artwork`}
                    fill
                    sizes="(max-width: 768px) 100vw, 400px"
                    className="object-cover group-hover:scale-105 transition-transform duration-700 brightness-[0.8] dark:brightness-[0.7]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card dark:from-[#0c1424] via-card/50 dark:via-[#0c1424]/50 to-transparent" />

                  {/* Header Overlay Content */}
                  <div className="absolute inset-x-4 bottom-3 flex items-end justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="h-10 w-10 rounded-xl bg-background/80 backdrop-blur-md border border-border/80 flex items-center justify-center text-xl shadow-lg shrink-0">
                        {pillar.icon}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-fantasy text-base sm:text-lg font-bold text-foreground truncate drop-shadow-sm">
                          {pillar.shortTitle}
                        </h3>
                        <p className="text-[11px] text-muted-foreground truncate max-w-[200px]">
                          {pillar.description}
                        </p>
                      </div>
                    </div>

                    <Badge
                      variant="outline"
                      className="text-[10px] px-2 py-0.5 bg-background/80 backdrop-blur-md border-border/80 text-foreground shrink-0 shadow-sm"
                    >
                      Hubs
                    </Badge>
                  </div>
                </div>

                {/* Franchise Links Grid */}
                <div className="p-4 flex-1 flex flex-col justify-between">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {pillar.franchises.map((f) => {
                      const subgroups = FRANCHISE_SUBGROUPS[f.slug] || [];
                      const hasSubgroups = subgroups.length > 1;

                      if (hasSubgroups) {
                        return (
                          <button
                            key={f.slug}
                            type="button"
                            onClick={() => {
                              setDrawerSearch("");
                              setActiveUniverseDrawer({
                                franchiseName: f.name,
                                subgroups,
                              });
                            }}
                            className="flex items-center justify-between py-2 px-3 rounded-xl border border-border/40 bg-muted/20 hover:bg-primary/10 hover:border-primary/50 text-left transition-all group/item w-full"
                          >
                            <span className="text-xs font-semibold text-foreground/90 group-hover/item:text-primary transition-colors truncate flex-1 pr-1">
                              {f.name}
                            </span>
                            <span className="text-muted-foreground/60 group-hover/item:text-primary transition-all text-xs font-bold shrink-0">
                              &gt;
                            </span>
                          </button>
                        );
                      }

                      return (
                        <Link
                          key={f.slug}
                          href={
                            f.categoryPrefix === "best"
                              ? `/best/${f.slug}`
                              : getSubcategoryUrl(f.categoryPrefix, f.slug)
                          }
                          className="flex items-center justify-between py-2 px-3 rounded-xl border border-border/40 bg-muted/20 hover:bg-primary/10 hover:border-primary/50 text-left transition-all group/item w-full"
                        >
                          <span className="text-xs font-semibold text-foreground/90 group-hover/item:text-primary transition-colors truncate flex-1 pr-1">
                            {f.name}
                          </span>
                          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40 group-hover/item:text-primary group-hover/item:translate-x-0.5 transition-all shrink-0" />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* SECTION: REGIONAL CULTURAL HUBS (FOR REAL NAMES) */}
      {category.slug === "real-names" && (
        <section className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <Compass className="w-5 h-5 text-primary shrink-0" />
              <div>
                <h2 className="text-lg sm:text-2xl font-bold font-fantasy text-foreground">
                  Cultural Spheres &amp; Regional Traditions
                </h2>
                <p className="text-xs text-muted-foreground">
                  Explore authentic naming genealogies categorized by continent and historical eras
                </p>
              </div>
            </div>
          </div>

          {/* Regional Filter Bar */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-2 scrollbar-none">
            <button
              onClick={() => setSelectedRegion("all")}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all shrink-0 flex items-center gap-1.5 ${
                selectedRegion === "all"
                  ? "bg-primary text-primary-foreground shadow-md shadow-primary/25"
                  : "bg-muted/40 hover:bg-muted text-muted-foreground hover:text-foreground border border-border/50"
              }`}
            >
              <span>🌐</span>
              <span>All Regions</span>
            </button>

            {REAL_NAME_PILLARS.map((pillar) => (
              <button
                key={pillar.id}
                onClick={() => setSelectedRegion(pillar.id)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all shrink-0 flex items-center gap-1.5 ${
                  selectedRegion === pillar.id
                    ? "bg-primary text-primary-foreground shadow-md shadow-primary/25"
                    : "bg-muted/40 hover:bg-muted text-muted-foreground hover:text-foreground border border-border/50"
                }`}
              >
                <span>{pillar.icon}</span>
                <span>{pillar.shortName}</span>
              </button>
            ))}
          </div>

          {/* Regional Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7">
            {filteredRealPillars.map((pillar) => (
              <motion.div
                key={pillar.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl border border-border/80 bg-card/90 dark:bg-[#0c1424]/90 backdrop-blur-xl overflow-hidden shadow-xl flex flex-col hover:border-primary/40 transition-all duration-300"
              >
                {/* Region Card Header with Custom Artwork */}
                <div className="relative h-36 sm:h-40 w-full overflow-hidden shrink-0 group">
                  <Image
                    src={pillar.image}
                    alt={`${pillar.name} cultural landscape`}
                    fill
                    sizes="(max-width: 768px) 100vw, 400px"
                    className="object-cover group-hover:scale-105 transition-transform duration-700 brightness-[0.8] dark:brightness-[0.7]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card dark:from-[#0c1424] via-card/50 dark:via-[#0c1424]/50 to-transparent" />

                  {/* Header Overlay Content */}
                  <div className="absolute inset-x-4 bottom-3 flex items-end justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="h-10 w-10 rounded-xl bg-background/80 backdrop-blur-md border border-border/80 flex items-center justify-center text-xl shadow-lg shrink-0">
                        {pillar.icon}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-fantasy text-base sm:text-lg font-bold text-foreground truncate drop-shadow-sm">
                          {pillar.name}
                        </h3>
                        <p className="text-[11px] text-muted-foreground truncate max-w-[220px]">
                          {pillar.tagline}
                        </p>
                      </div>
                    </div>

                    <Badge
                      variant="outline"
                      className="text-[10px] px-2 py-0.5 bg-background/80 backdrop-blur-md border-border/80 text-foreground shrink-0 shadow-sm"
                    >
                      Traditions
                    </Badge>
                  </div>
                </div>

                {/* Cultural Generator Links Grid */}
                <div className="p-4 flex-1 flex flex-col justify-between">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {pillar.subcategories.slice(0, 10).map((sub) => (
                      <Link
                        key={sub.slug}
                        href={getSubcategoryUrl("real-names", sub.slug)}
                        className="flex items-center justify-between py-2 px-3 rounded-xl border border-border/40 bg-muted/20 hover:bg-primary/10 hover:border-primary/50 text-left transition-all group/item w-full"
                      >
                        <span className="text-xs font-semibold text-foreground/90 group-hover/item:text-primary transition-colors truncate flex-1 pr-1">
                          {sub.name}
                        </span>
                        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40 group-hover/item:text-primary group-hover/item:translate-x-0.5 transition-all shrink-0" />
                      </Link>
                    ))}
                  </div>

                  {pillar.subcategories.length > 10 && (
                    <button
                      type="button"
                      onClick={() => setSelectedRegion(pillar.id)}
                      className="mt-3 pt-2.5 border-t border-border/40 text-xs font-semibold text-primary hover:underline flex items-center justify-center gap-1 w-full"
                    >
                      <span>Explore all {pillar.shortName} traditions</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* SECTION 2: COMPLETE ALPHABETICAL (A-Z) DIRECTORY AT THE BOTTOM */}
      <section className="space-y-6 pt-4 border-t border-border/60">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-primary" />
            <div>
              <h2 className="text-lg sm:text-xl font-bold font-fantasy text-foreground">
                All {category.name} A-Z Directory
              </h2>
              <p className="text-xs text-muted-foreground">
                Browse all individual naming generators and lore archives
              </p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative w-full sm:max-w-xs">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2" />
            <Input
              type="text"
              placeholder={`Search all ${category.name}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-9 text-xs rounded-xl bg-card border-border/80 h-10"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Alphabet Bar */}
        <div className="flex items-center gap-1 overflow-x-auto pb-2 scrollbar-none">
          <button
            onClick={() => setSelectedLetter("all")}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all shrink-0 ${
              selectedLetter === "all"
                ? "bg-primary text-primary-foreground shadow-sm shadow-primary/20"
                : "bg-muted/40 hover:bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            All
          </button>
          {ALPHABET.map((letter) => (
            <button
              key={letter}
              onClick={() => setSelectedLetter(letter)}
              className={`w-7 h-7 rounded-xl text-xs font-bold transition-all flex items-center justify-center shrink-0 ${
                selectedLetter === letter
                  ? "bg-primary text-primary-foreground shadow-sm shadow-primary/20"
                  : "bg-muted/40 hover:bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {letter}
            </button>
          ))}
        </div>

        {/* Subcategories Grid */}
        {groupedByLetter.length > 0 ? (
          <div className="space-y-8">
            {groupedByLetter.map(([letter, items]) => (
              <div key={letter} className="space-y-3">
                {/* Letter Header */}
                <div className="flex items-center gap-3 pb-2 border-b border-border/60">
                  <span className="h-8 w-8 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center font-fantasy font-bold text-sm">
                    {letter}
                  </span>
                  <span className="text-xs font-semibold text-muted-foreground">
                    Collections
                  </span>
                </div>

                {/* Subcategories Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {items.map((sub) => {
                    const icon = getSubcategoryIcon(sub.name || sub.slug, category.slug);

                    return (
                      <Link
                        key={sub.slug}
                        href={getSubcategoryUrl(category.slug, sub.slug)}
                        className="group flex items-center justify-between p-3.5 rounded-2xl border border-border/70 bg-card/70 hover:bg-card hover:border-primary/50 hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex items-center gap-3 min-w-0 pr-2">
                          <span className="text-2xl shrink-0 group-hover:scale-110 transition-transform">
                            {icon}
                          </span>
                          <div className="min-w-0">
                            <h3 className="text-xs font-bold text-foreground group-hover:text-primary transition-colors truncate">
                              {sub.name}
                            </h3>
                            {sub.description ? (
                              <p className="text-[11px] text-muted-foreground truncate max-w-[190px]">
                                {sub.description}
                              </p>
                            ) : (
                              <p className="text-[10px] text-muted-foreground/80">
                                Generate lore names &amp; meanings
                              </p>
                            )}
                          </div>
                        </div>

                        <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0 ml-2" />
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-16 text-center rounded-3xl border border-dashed border-border/80 bg-card/40">
            <div className="text-3xl mb-2">🔍</div>
            <h3 className="text-base font-bold font-fantasy text-foreground mb-1">
              No collections found in {category.name}
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              Try adjusting your search terms or letter filter.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSearch("");
                setSelectedLetter("all");
              }}
            >
              Clear Filters
            </Button>
          </div>
        )}
      </section>

      {/* Universe Sub-Generators Modal / Dialog */}
      <AnimatePresence>
        {activeUniverseDrawer && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/75 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-2xl max-h-[85vh] rounded-3xl border border-border/80 bg-card p-6 sm:p-8 shadow-2xl overflow-hidden flex flex-col"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between pb-4 border-b border-border/60">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-2xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center text-xl shrink-0">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold font-fantasy text-foreground">
                      {activeUniverseDrawer.franchiseName}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Dedicated Species &amp; Name Generators
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setActiveUniverseDrawer(null)}
                  className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Sub-generator Search */}
              <div className="relative my-4">
                <Search className="w-4 h-4 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2" />
                <Input
                  type="text"
                  placeholder={`Search ${activeUniverseDrawer.franchiseName} races & factions...`}
                  value={drawerSearch}
                  onChange={(e) => setDrawerSearch(e.target.value)}
                  className="pl-10 h-10 text-xs rounded-xl"
                  autoFocus
                />
              </div>

              {/* Species & Races Grid */}
              <div className="flex-1 overflow-y-auto pr-1 grid grid-cols-2 sm:grid-cols-3 gap-2 py-1 scrollbar-thin scrollbar-thumb-primary/20">
                {filteredDrawerSubgroups.map((sub) => (
                  <Link
                    key={sub.slug}
                    href={sub.url}
                    onClick={() => setActiveUniverseDrawer(null)}
                    className="p-3 rounded-xl border border-border/60 bg-muted/20 hover:bg-primary/10 hover:border-primary/50 text-xs font-semibold text-foreground hover:text-primary transition-all flex items-center justify-between group"
                  >
                    <span className="truncate pr-1">{sub.name}</span>
                    <ArrowRight className="w-3 h-3 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
                  </Link>
                ))}
              </div>

              {/* Modal Footer */}
              <div className="pt-4 mt-4 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground">
                <span>Select any race to start generating names &amp; lore</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveUniverseDrawer(null)}
                  className="rounded-xl text-xs"
                >
                  Close
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
